// Guided tutorials ("tours"): a spotlight + coach-mark card that walks the user
// through the REAL app (no fake demo screens). Generic engine only - the tour
// content lives in tours.js and reaches the app through the `api` object the
// controller passes to init(), so this module never touches the model.
//
// Step shape (every field may also be a function of `api`, evaluated live):
//   id        unique within the tour
//   chapter   chapter id (tour.chapters) - resume restarts at a chapter's start
//   title / body / tip   copy (authored HTML, never user data)
//   target    selector | selector[] | () => Element. First VISIBLE match wins,
//             re-resolved every frame, so a list lets the spotlight hop onto a
//             sub-modal the moment it opens (e.g. the variant picker).
//   mode      'next' (default: Next button) | 'click' (advance when the target
//             is clicked) | 'wait' (advance on `signal` / `until`)
//   signal    name or names that advance a 'wait' step (Tour.signal from the controller)
//   until     () => bool, polled; true advances
//   skipIf    () => bool, checked on arrival (already-satisfied steps are skipped)
//   next      step id to jump to instead of the following step
//   action    { label, run } extra card button (e.g. "Open it now")
//   block     false lets clicks through outside the spotlight (default true).
//             'next' steps are look-only: even the spotlit element is blocked.
//
//   onEnter   (api) => void, runs on arrival (navigate the panel for this step)
//
// Tour-level: practice (controller swaps real sales for pretend ones while it
// runs, see practiceMode), onStart(api) (load pretend data before the panel
// opens), onEnd(api) (cleanup however the tour ends), resumable: false
// (always restart from step 1 - for tours built on rebuilt practice data).
// Steps can't be skipped one by one - only the whole tour, which then
// spotlights the ? button so the user knows how to come back.
//   placement 'bottom' | 'top' | 'right' | 'left' preference for the card
//
// Progress is per user, per browser (localStorage, keyed by user id so one
// account's progress never leaks into another on a shared terminal).

const STORE_PREFIX = "pointbunny_tours_v1:";
const MISSING_GRACE_MS = 700; // a target that vanishes briefly (re-render, modal swap) keeps its spot
const PAD = 8;

const visible = (el) => {
  if (!el || !el.isConnected) return false;
  const r = el.getBoundingClientRect();
  if (r.width < 2 || r.height < 2) return false;
  // Above/below the fold still counts (it can be scrolled into view), but
  // fully off to the side means an off-canvas drawer or slide-out that is
  // closed - never a target.
  if (r.right <= 0 || r.left >= window.innerWidth) return false;
  const cs = getComputedStyle(el);
  if (cs.visibility === "hidden" || cs.display === "none") return false;
  return !covered(el, r);
};

// Something else painted on top of it (e.g. the navbar "?" while a
// full-screen panel is open)? Checked at the element's centre, ignoring the
// tour's own layers. Below-the-fold centres can't be probed - not covered.
const covered = (el, r) => {
  const x = r.left + r.width / 2;
  const y = r.top + r.height / 2;
  if (x < 0 || y < 0 || x > window.innerWidth || y > window.innerHeight) return false;
  const top = document.elementsFromPoint(x, y).find((n) => !n.closest(".tour-root"));
  return !!top && top !== el && !el.contains(top) && !top.contains(el);
};

class TourEngine {
  _tours = new Map();
  _order = [];
  _api = {};
  _userId = null;
  _store = { offered: {}, tours: {} };
  _persist = null;
  _syncTimer = null;
  _run = null;
  _els = null;
  _raf = 0;
  _lastLogic = 0;
  _offerTimer = null;
  _hubEl = null;

  // ── Setup ──────────────────────────────────────────────────────────────────

  init({ tours, api, userId, remote = null, persist = null }) {
    this._api = api ?? {};
    this._tours.clear();
    this._order = [];
    (tours ?? []).forEach((t) => { this._tours.set(t.id, t); this._order.push(t.id); });
    this._userId = userId ?? "anon";
    this._persist = persist;
    this._store = this._merge(this._load(), remote);
    try { localStorage.setItem(this._key(), JSON.stringify(this._store)); } catch (_) {}
    // This browser knew something the account didn't (progress from before
    // syncing existed, or a save that never made it): push it up.
    if (persist && JSON.stringify(this._store) !== JSON.stringify(this._merge(this._blank(), remote))) this._queueSync();
    this._wireGlobal();
    this.mountHelpButtons();
    this._syncHubBtn();
  }

  // Stops everything silently (sign-out). Progress already saved stays saved.
  stop() {
    this._flushSync();
    clearInterval(this._offerTimer);
    this._offerTimer = null;
    if (this._run) this._teardown();
    this._closeHub();
  }

  isRunning() { return !!this._run; }

  // ── Persistence ────────────────────────────────────────────────────────────

  _key() { return STORE_PREFIX + this._userId; }

  _blank() { return { offered: {}, tours: {} }; }

  _load() {
    try {
      const raw = JSON.parse(localStorage.getItem(this._key()) ?? "null");
      if (raw && typeof raw === "object") return { offered: raw.offered ?? {}, tours: raw.tours ?? {} };
    } catch (_) {}
    return this._blank();
  }

  // Combines this browser's copy with the account's synced copy. Whichever
  // has got further wins per tour (done > skipped > paused > active > new),
  // finished chapters are unioned, and "offered" sticks once either side set
  // it - so a tour finished or dismissed anywhere never auto-starts again.
  _merge(local, remote) {
    const rank = { new: 0, active: 1, paused: 2, skipped: 3, done: 4 };
    const out = { offered: { ...(local.offered ?? {}) }, tours: { ...(local.tours ?? {}) } };
    if (!remote || typeof remote !== "object") return out;
    Object.entries(remote.offered ?? {}).forEach(([id, v]) => { if (v) out.offered[id] = true; });
    Object.entries(remote.tours ?? {}).forEach(([id, r]) => {
      if (!r || typeof r !== "object") return;
      const l = out.tours[id];
      if (!l) { out.tours[id] = r; return; }
      const winner = (rank[r.status] ?? 0) > (rank[l.status] ?? 0) ? r : l;
      out.tours[id] = { ...winner, chapters: [...new Set([...(l.chapters ?? []), ...(r.chapters ?? [])])] };
    });
    return out;
  }

  _save() {
    try { localStorage.setItem(this._key(), JSON.stringify(this._store)); } catch (_) {}
    this._syncHubBtn();
    this._queueSync();
  }

  // Account sync is debounced: steps save on every move, the account only
  // needs to hear about it once things settle.
  _queueSync() {
    if (!this._persist) return;
    clearTimeout(this._syncTimer);
    this._syncTimer = setTimeout(() => this._flushSync(), 1500);
  }

  _flushSync() {
    if (!this._persist || !this._syncTimer) return;
    clearTimeout(this._syncTimer);
    this._syncTimer = null;
    Promise.resolve(this._persist(this._store)).catch(() => {});
  }

  progress(id) {
    const p = this._store.tours[id] ?? {};
    return { status: p.status ?? "new", step: p.step ?? null, chapters: p.chapters ?? [] };
  }

  _setProgress(id, patch) {
    this._store.tours[id] = { ...this.progress(id), ...patch };
    this._save();
  }

  // ── Starting / resuming ────────────────────────────────────────────────────

  // Offers a tour once per user: waits until the app is idle (no onboarding,
  // PIN setup, or open modal) and then opens the tour's first step, which is
  // expected to be a welcome card with a "Maybe later" option.
  offer(id) {
    const tour = this._tours.get(id);
    if (!tour || this._store.offered[id] || this.progress(id).status !== "new") return;
    if (tour.can && !tour.can(this._api)) return;
    clearInterval(this._offerTimer);
    let calm = 0;
    this._offerTimer = setInterval(() => {
      if (this._run) return;
      calm = this._api.isIdle?.() === false ? 0 : calm + 1;
      if (calm < 2) return;
      clearInterval(this._offerTimer);
      this._offerTimer = null;
      this._store.offered[id] = true;
      this._save();
      this.start(id, { fromStart: true });
    }, 800);
  }

  // Starts (or resumes) a tour. Resuming restarts at the beginning of the
  // chapter that was in progress; steps that are already satisfied are
  // skipped automatically by their skipIf.
  async start(id, { fromStart = false, chapter = null } = {}) {
    const tour = this._tours.get(id);
    if (!tour) return;
    if (tour.can && !tour.can(this._api)) {
      this._api.toast?.("You don't have access to this part of the app yet.", "info");
      return;
    }
    if (this._run) this.pause({ quiet: true });
    this._closeHub();

    // Practice tours load their pretend data before the panel opens (the
    // matching onEnd puts the real data back, see _teardown).
    if (tour.onStart) this._safe(() => tour.onStart(this._api));
    if (tour.open && !(tour.isOpen?.(this._api))) {
      try { await tour.open(this._api); } catch (_) {}
      await new Promise((r) => setTimeout(r, 350));
    }

    const steps = tour.steps;
    let idx = 0;
    if (chapter) {
      idx = Math.max(0, steps.findIndex((s) => s.chapter === chapter));
    } else if (!fromStart && tour.resumable !== false) {
      // Practice tours (resumable: false) always restart: their pretend data
      // is rebuilt fresh, so a mid-story step would have nothing to point at.
      const p = this.progress(id);
      if (p.status !== "done" && p.step) {
        const at = steps.findIndex((s) => s.id === p.step);
        if (at > 0) idx = this._chapterEntry(tour, at);
      }
    }

    this._run = { tour, idx: -1, history: [], targetEl: null, missingSince: 0, lastRect: null, scrolled: false };
    this._build();
    this._setProgress(id, { status: "active" });
    this._goTo(idx, { push: false });
  }

  // Index of the first step of the chapter containing `at`, stepping back one
  // chapter at a time while a chapter's `entry` check says the app isn't in
  // the state that chapter starts from (e.g. resuming "checkout" from home).
  _chapterEntry(tour, at) {
    const chapters = tour.chapters ?? [];
    let ch = tour.steps[at].chapter;
    if (!ch) return at;
    let ci = chapters.findIndex((c) => c.id === ch);
    while (ci > 0 && chapters[ci].entry && !chapters[ci].entry(this._api)) ci--;
    ch = chapters[ci]?.id ?? ch;
    return Math.max(0, tour.steps.findIndex((s) => s.chapter === ch));
  }

  // The x: saves the spot, then points at the ? so they know how to come back.
  pause({ quiet = false } = {}) {
    if (!this._run) return;
    const { tour } = this._run;
    if (tour.ephemeral) { this._teardown(); return; }
    this._setProgress(tour.id, { status: "paused", step: this._step()?.id ?? null });
    this._teardown();
    if (!quiet) {
      this._showReminder("Paused, not lost", tour.resumable === false
        // Practice tours rebuild their pretend data, so they start over.
        ? "<p>Tap this <strong>?</strong> anytime to run this tutorial again. Every tutorial is also listed under the <strong>?</strong> at the top of the home screen.</p>"
        : "<p>Tap this <strong>?</strong> anytime to pick up right where you left off, or to replay any part.</p>");
    }
  }

  // "Skip tutorial" / "Maybe later": ends the whole tour, same pointer to the ?.
  skip() {
    if (!this._run) return;
    const { tour } = this._run;
    this._setProgress(tour.id, { status: "skipped", step: this._step()?.id ?? null });
    this._teardown();
    this._showReminder("No problem!",
      "<p>Whenever you want the tour, open <strong>Tutorials</strong> from here. You can start it again or jump straight to any part.</p>");
  }

  // True while a tour that wants pretend data (the first-sale tour) is on
  // screen - the controller swaps the real sale for a practice one.
  practiceMode() {
    return !!this._run?.tour.practice;
  }

  // One-card, unsaved "tour" that spotlights the ? button (or the mobile menu
  // button, where the ? lives on phones).
  _showReminder(title, body) {
    const tour = {
      id: "_reminder",
      ephemeral: true,
      title: "Tutorials",
      icon: "🎓",
      // Tutorials lives in the top-bar profile menu: spotlight the profile
      // button (phones: the menu button) - both checked BEFORE .tour-help-btn,
      // or on home the Active Queue's own ? wins. When a full-screen panel
      // covers the top bar, that panel's ? next to its title.
      steps: [{ id: "reminder", reminder: true, target: ["#tourHubBtn", "#navProfileBtn", "#navMenuBtn", ".tour-help-btn"], title, body, nextLabel: "Got it" }],
    };
    this._run = { tour, idx: -1, history: [], targetEl: null, missingSince: 0, lastRect: null, scrolled: false };
    this._build();
    this._goTo(0, { push: false });
    this._pulseHubBtn();
  }

  // Controller hook: "the user just did X". Advances the current 'wait' step,
  // or - for a fast user who got ahead of the card - jumps past a step a little
  // further on that was waiting for the same thing.
  signal(name) {
    if (!this._run) return;
    const { tour, idx } = this._run;
    const wants = (s) => [].concat(this._val(s.signal) ?? []).includes(name);
    const cur = tour.steps[idx];
    if (cur && this._mode(cur) === "wait" && wants(cur)) { setTimeout(() => this._next(), 120); return; }
    for (let j = idx + 1; j <= idx + 3 && j < tour.steps.length; j++) {
      const s = tour.steps[j];
      if (s.chapter !== cur?.chapter) break;
      if (wants(s)) { setTimeout(() => this._goTo(j + 1), 120); return; }
    }
  }

  // ── Step navigation ────────────────────────────────────────────────────────

  _val(v) { return typeof v === "function" ? v(this._api) : v; }
  _step() { return this._run ? this._run.tour.steps[this._run.idx] : null; }
  _mode(step) { return this._val(step.mode) ?? "next"; }

  _next() {
    if (!this._run) return;
    const step = this._step();
    const jump = this._val(step?.next);
    const to = jump ? this._run.tour.steps.findIndex((s) => s.id === jump) : this._run.idx + 1;
    this._goTo(to < 0 ? this._run.idx + 1 : to);
  }

  _back() {
    if (!this._run || !this._run.history.length) return;
    const prev = this._run.history.pop();
    this._goTo(prev, { push: false, noSkip: true });
  }

  _goTo(idx, { push = true, noSkip = false } = {}) {
    const run = this._run;
    if (!run) return;
    const steps = run.tour.steps;
    if (!noSkip) {
      while (idx < steps.length && steps[idx].skipIf && this._safe(() => steps[idx].skipIf(this._api))) idx++;
    }
    const prevStep = steps[run.idx];
    if (idx >= steps.length) { this._finish(); return; }
    if (push && run.idx >= 0) run.history.push(run.idx);
    // Leaving a chapter forward marks it done.
    if (prevStep?.chapter && prevStep.chapter !== steps[idx].chapter && idx > run.idx) {
      this._markChapter(run.tour.id, prevStep.chapter);
    }
    run.idx = idx;
    run.targetEl = null;
    run.missingSince = performance.now();
    run.scrolled = false;
    run.lastRect = null;
    if (!run.tour.ephemeral) this._setProgress(run.tour.id, { status: "active", step: steps[idx].id });
    // Step setup (e.g. switch the panel to the section this step talks about).
    if (steps[idx].onEnter) this._safe(() => steps[idx].onEnter(this._api));
    this._glide();
    this._renderCard();
    this._tick(true);
  }

  _markChapter(tourId, ch) {
    const chapters = new Set(this.progress(tourId).chapters);
    chapters.add(ch);
    this._setProgress(tourId, { chapters: [...chapters] });
  }

  _finish() {
    const { tour } = this._run;
    if (!tour.ephemeral) {
      const all = (tour.chapters ?? []).map((c) => c.id);
      this._setProgress(tour.id, { status: "done", step: null, chapters: all });
    }
    this._teardown();
  }

  _safe(fn) { try { return fn(); } catch (_) { return false; } }

  // ── DOM: overlay, spotlight, blockers, card ────────────────────────────────

  _build() {
    if (this._els) return;
    const root = document.createElement("div");
    root.className = "tour-root";
    root.innerHTML = `
      <div class="tour-block" data-b="t"></div>
      <div class="tour-block" data-b="r"></div>
      <div class="tour-block" data-b="b"></div>
      <div class="tour-block" data-b="l"></div>
      <div class="tour-spot" aria-hidden="true"></div>
      <div class="tour-card" role="dialog" aria-modal="false" aria-labelledby="tourCardTitle" tabindex="-1"></div>`;
    document.body.appendChild(root);
    const blocks = [...root.querySelectorAll(".tour-block")];
    blocks.forEach((b) => b.addEventListener("click", (e) => { e.preventDefault(); e.stopPropagation(); this._nudge(); }));
    const card = root.querySelector(".tour-card");
    card.addEventListener("click", (e) => this._onCardClick(e));
    this._els = { root, blocks, spot: root.querySelector(".tour-spot"), card };
    document.body.classList.add("tour-active");
    const loop = (t) => { this._tick(false, t); this._raf = requestAnimationFrame(loop); };
    this._raf = requestAnimationFrame(loop);
  }

  _teardown() {
    const tour = this._run?.tour;
    cancelAnimationFrame(this._raf);
    this._els?.root.remove();
    this._els = null;
    this._run = null;
    document.body.classList.remove("tour-active");
    // Tours that create pretend data clean it up however they end
    // (finished, skipped, closed, or replaced by another tour).
    if (tour?.onEnd) this._safe(() => tour.onEnd(this._api));
  }

  _renderCard() {
    const run = this._run;
    const step = this._step();
    if (!run || !step || !this._els) return;
    const { tour } = run;
    const mode = this._mode(step);
    const chapters = tour.chapters ?? [];
    const ci = chapters.findIndex((c) => c.id === step.chapter);
    const chip = ci >= 0
      ? `${tour.icon ?? ""} Part ${ci + 1} of ${chapters.length} · ${chapters[ci].title}`
      : `${tour.icon ?? ""} ${tour.title}`;
    const pct = Math.round(((run.idx + 1) / tour.steps.length) * 100);
    const isLast = run.idx === tour.steps.length - 1;
    const tip = this._val(step.tip);
    const action = this._val(step.action);
    // No Back on a step that navigated somewhere (onEnter): going back would
    // show the previous card without undoing the navigation.
    const canBack = run.history.length > 0 && mode === "next" && !step.onEnter
      && this._mode(tour.steps[run.history[run.history.length - 1]]) === "next";
    const doHint = mode === "click" ? "Tap the highlighted spot to continue"
      : mode === "wait" ? (this._val(step.waitHint) ?? "Go ahead, the tutorial follows along")
      : "";
    // Steps are never skippable one by one - the only way out is the whole
    // tutorial (which then points at the ? to come back).
    const skipBtn = step.reminder ? ""
      : step.welcome
        ? `<button class="tour-btn tour-btn--ghost" data-act="skip" type="button">Maybe later</button>`
        : `<button class="tour-btn tour-btn--ghost" data-act="skip" type="button">Skip tutorial</button>`;
    const backBtn = canBack ? `<button class="tour-btn tour-btn--ghost" data-act="back" type="button">Back</button>` : "";
    const primary = mode === "next"
      ? `<button class="tour-btn tour-btn--primary" data-act="next" type="button">${this._val(step.nextLabel) ?? (isLast ? "Finish" : "Next")}</button>`
      : "";

    this._els.card.className = `tour-card${step.welcome ? " tour-card--welcome" : ""}`;
    this._els.card.innerHTML = `
      <div class="tour-card-top">
        <span class="tour-card-chip">${chip}</span>
        <button class="tour-card-x" data-act="close" type="button" aria-label="Close tutorial" title="${tour.ephemeral ? "Close" : "Close (your progress is saved)"}">&times;</button>
      </div>
      ${step.welcome && tour.hero ? `<div class="tour-card-hero" aria-hidden="true">${tour.hero}</div>` : ""}
      <h3 class="tour-card-title" id="tourCardTitle">${this._val(step.title) ?? ""}</h3>
      <div class="tour-card-body">${this._val(step.body) ?? ""}</div>
      ${tip ? `<div class="tour-card-tip"><span class="tour-card-tip-icon" aria-hidden="true">💡</span><div>${tip}</div></div>` : ""}
      ${doHint ? `<div class="tour-card-do"><span class="tour-card-do-emoji" aria-hidden="true">👆</span>${doHint}</div>` : ""}
      ${action ? `<button class="tour-btn tour-btn--soft tour-card-action" data-act="action" type="button">${action.label}</button>` : ""}
      <div class="tour-card-foot">
        ${tour.ephemeral ? "" : `<div class="tour-card-bar" aria-hidden="true"><span style="width:${pct}%"></span></div>`}
        <div class="tour-card-btns">
          ${skipBtn}
          <span class="tour-card-spacer"></span>
          ${backBtn}
          ${primary}
        </div>
      </div>`;
    this._els.card.classList.remove("tour-card--in");
    void this._els.card.offsetWidth;
    this._els.card.classList.add("tour-card--in");
    requestAnimationFrame(() => {
      const focusEl = this._els?.card.querySelector('[data-act="next"]') ?? this._els?.card;
      focusEl?.focus({ preventScroll: true });
    });
  }

  _onCardClick(e) {
    const btn = e.target.closest("[data-act]");
    if (!btn || !this._run) return;
    const act = btn.dataset.act;
    if (act === "next") this._next();
    else if (act === "back") this._back();
    else if (act === "close") this.pause();
    else if (act === "skip") this.skip();
    else if (act === "action") this._safe(() => this._val(this._step().action)?.run(this._api));
  }

  _nudge() {
    const card = this._els?.card;
    if (!card) return;
    card.classList.remove("tour-card--nudge");
    void card.offsetWidth;
    card.classList.add("tour-card--nudge");
  }

  // ── Frame loop: resolve target, position spotlight + card, poll conditions ──

  _resolveTarget(step) {
    const t = this._val(step.target);
    if (!t) return null;
    for (const s of [].concat(t)) {
      if (!s) continue;
      const list = typeof s === "string" ? document.querySelectorAll(s) : [s];
      for (const el of list) if (visible(el) && !this._els?.root.contains(el)) return el;
    }
    return null;
  }

  _tick(force, now = performance.now()) {
    const run = this._run;
    if (!run || !this._els) return;
    const step = this._step();
    if (!step) return;
    const mode = this._mode(step);
    const wantsTarget = !!step.target;
    const el = wantsTarget ? this._resolveTarget(step) : null;

    if (el) {
      run.missingSince = 0;
      if (run.targetEl !== el) { run.targetEl = el; run.scrolled = false; this._glide(); }
      if (!run.scrolled) {
        run.scrolled = true;
        const r = el.getBoundingClientRect();
        if (r.top < 60 || r.bottom > window.innerHeight - 20) {
          el.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" });
        }
      }
      const r = el.getBoundingClientRect();
      const rect = {
        top: Math.max(4, r.top - PAD), left: Math.max(4, r.left - PAD),
        right: Math.min(window.innerWidth - 4, r.right + PAD), bottom: Math.min(window.innerHeight - 4, r.bottom + PAD),
      };
      run.lastRect = rect;
      this._layout(rect, step, mode, force);
    } else if (wantsTarget && run.lastRect && (!run.missingSince || now - run.missingSince < MISSING_GRACE_MS)) {
      // Brief gap (re-render, modal swap): hold the last position.
      if (!run.missingSince) run.missingSince = now;
    } else {
      if (wantsTarget && !run.missingSince) run.missingSince = now;
      run.targetEl = null;
      run.lastRect = null;
      this._layout(null, step, mode, force);
    }

    if (now - this._lastLogic > 250) {
      this._lastLogic = now;
      if (mode === "wait" && step.until && this._safe(() => step.until(this._api))) this._next();
    }
  }

  // The spotlight + card glide only when they move to a NEW target. The rest
  // of the time they track their target frame by frame with no easing, so a
  // modal sliding in never leaves the highlight trailing off-center.
  _glide() {
    const root = this._els?.root;
    if (!root) return;
    root.classList.add("tour-root--glide");
    clearTimeout(this._glideTimer);
    this._glideTimer = setTimeout(() => root.classList.remove("tour-root--glide"), 340);
  }

  _layout(rect, step, mode, force) {
    const { spot, blocks, card, root } = this._els;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const block = this._val(step.block) !== false;
    // No target: intro/outro cards dim the whole screen; an action step whose
    // target hasn't appeared stays out of the way so the user can recover.
    const dimAll = !rect && mode === "next";
    root.classList.toggle("tour-root--clear", !rect && !dimAll);

    if (rect) {
      Object.assign(spot.style, {
        top: `${rect.top}px`, left: `${rect.left}px`,
        width: `${rect.right - rect.left}px`, height: `${rect.bottom - rect.top}px`,
      });
      spot.classList.remove("tour-spot--none");
    } else {
      Object.assign(spot.style, { top: `${vh / 2}px`, left: `${vw / 2}px`, width: "0px", height: "0px" });
      spot.classList.toggle("tour-spot--none", !dimAll);
    }

    const on = block && (rect || dimAll);
    // "Look at this" steps (Next button) keep the spotlight look-only: clicking
    // the highlighted thing would wander off the script (e.g. Upload CSV is a
    // recommendation, not an action). Only click/wait steps open the hole.
    const h = rect && mode !== "next" ? rect : { top: vh, left: vw, right: vw, bottom: vh };
    const set = (b, top, left, w, ht) => Object.assign(b.style, {
      display: on ? "block" : "none", top: `${top}px`, left: `${left}px`, width: `${Math.max(0, w)}px`, height: `${Math.max(0, ht)}px`,
    });
    set(blocks[0], 0, 0, vw, h.top);
    set(blocks[1], h.top, h.right, vw - h.right, h.bottom - h.top);
    set(blocks[2], h.bottom, 0, vw, vh - h.bottom);
    set(blocks[3], h.top, 0, h.left, h.bottom - h.top);

    this._placeCard(card, rect, this._val(step.placement), mode !== "next");
  }

  _placeCard(card, rect, pref, interactive) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const m = 12;
    const gap = 14;
    const cw = card.offsetWidth;
    const ch = card.offsetHeight;
    let top;
    let left;

    if (vw < 640) {
      // Phones: dock to whichever edge leaves the target uncovered. A target
      // taller than the room left over (the item sheet, the whole item grid)
      // can't avoid overlap, so the card shrinks to its title + hint and docks
      // at the TOP: sheets and modals keep their primary action (Add to cart,
      // Save) at the bottom, and a card docked there would block the very tap
      // the step is waiting for.
      card.classList.add("tour-card--docked");
      card.classList.remove("tour-card--compact");
      let dockTop = false;
      if (rect) {
        const fullH = card.offsetHeight;
        if (vh - rect.bottom >= fullH + m) dockTop = false;
        else if (rect.top >= fullH + m) dockTop = true;
        else {
          dockTop = true;
          if (interactive) card.classList.add("tour-card--compact");
        }
      }
      const h = card.offsetHeight;
      top = dockTop ? m : vh - h - m;
      left = m;
      card.style.top = `${Math.round(Math.max(m, top))}px`;
      card.style.left = `${m}px`;
      return;
    } else {
      card.classList.remove("tour-card--docked", "tour-card--compact");
      if (!rect) {
        top = (vh - ch) / 2;
        left = (vw - cw) / 2;
      } else {
        const cx = (rect.left + rect.right) / 2;
        const cy = (rect.top + rect.bottom) / 2;
        const opts = {
          bottom: { fits: rect.bottom + gap + ch <= vh - m, space: vh - rect.bottom, top: rect.bottom + gap, left: cx - cw / 2 },
          top: { fits: rect.top - gap - ch >= m, space: rect.top, top: rect.top - gap - ch, left: cx - cw / 2 },
          right: { fits: rect.right + gap + cw <= vw - m, space: vw - rect.right, top: cy - ch / 2, left: rect.right + gap },
          left: { fits: rect.left - gap - cw >= m, space: rect.left, top: cy - ch / 2, left: rect.left - gap - cw },
        };
        const order = [pref, "bottom", "right", "top", "left"].filter((k, i, a) => k && opts[k] && a.indexOf(k) === i);
        let pick = order.find((k) => opts[k].fits);
        if (!pick) {
          // Huge target (a whole form): sit in the corner with the most room.
          pick = null;
          top = vh - ch - m;
          left = vw - cw - m;
          if (rect.left > vw - rect.right) left = m;
        } else {
          top = opts[pick].top;
          left = opts[pick].left;
        }
      }
    }
    top = Math.max(m, Math.min(top, vh - ch - m));
    left = Math.max(m, Math.min(left, vw - cw - m));
    card.style.top = `${Math.round(top)}px`;
    card.style.left = `${Math.round(left)}px`;
  }

  // ── Global listeners ───────────────────────────────────────────────────────

  _wireGlobal() {
    if (this._wired) return;
    this._wired = true;

    // Click-to-advance steps: let the app handle the click first, then move on.
    document.addEventListener("click", (e) => {
      const run = this._run;
      if (!run || !run.targetEl || this._els?.root.contains(e.target)) return;
      if (!run.targetEl.contains(e.target)) return;
      if (this._mode(this._step()) === "click") setTimeout(() => this._next(), 60);
    }, true);

    document.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;
      if (this._hubEl) { this._closeHub(); return; }
      if (this._run) { e.stopPropagation(); this.pause(); }
    }, true);

    // "?" help buttons, wherever they are mounted.
    document.addEventListener("click", (e) => {
      const btn = e.target.closest(".tour-help-btn, [data-tour-hub]");
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      if (btn.hasAttribute("data-tour-hub")) { this.openHub(); return; }
      const id = btn.dataset.tour;
      const chapter = btn.dataset.tourChapter || null;
      this.start(id, chapter ? { chapter } : { fromStart: this.progress(id).status === "done" });
    });

    // Re-mount help buttons into views that re-render their markup.
    const mo = new MutationObserver(() => this.mountHelpButtons());
    document.querySelectorAll(".modal-parent").forEach((n) => mo.observe(n, { childList: true, subtree: false }));
  }

  // Each tour may declare helpAnchor (a heading selector) - a small "?" is
  // appended inside it. Idempotent; safe to call on every re-render.
  mountHelpButtons() {
    this._tours.forEach((tour) => {
      [].concat(tour.helpAnchors ?? []).forEach(({ selector, chapter }) => {
        document.querySelectorAll(selector).forEach((h) => {
          if (h.querySelector(":scope > .tour-help-btn")) return;
          if (tour.can && !this._safe(() => tour.can(this._api))) return;
          const b = document.createElement("button");
          b.type = "button";
          b.className = "tour-help-btn";
          b.dataset.tour = tour.id;
          if (chapter) b.dataset.tourChapter = chapter;
          b.setAttribute("aria-label", `Show me how: ${tour.title}`);
          b.title = "Show me how";
          b.textContent = "?";
          h.appendChild(b);
        });
      });
    });
  }

  // ── Hub button state + Tutorials hub ───────────────────────────────────────

  _syncHubBtn() {
    const btn = document.getElementById("tourHubBtn");
    if (!btn) return;
    const main = this._order.map((id) => this._tours.get(id)).find((t) => t.featured);
    const st = main ? this.progress(main.id).status : "done";
    btn.classList.toggle("tour-hub-btn--dot", st !== "done");
  }

  _pulseHubBtn() {
    const btn = document.getElementById("tourHubBtn");
    if (!btn) return;
    btn.classList.remove("tour-hub-btn--pulse");
    void btn.offsetWidth;
    btn.classList.add("tour-hub-btn--pulse");
    setTimeout(() => btn.classList.remove("tour-hub-btn--pulse"), 3200);
  }

  _statusChip(st) {
    const map = {
      new: ["New", "new"], active: ["In progress", "progress"], paused: ["In progress", "progress"],
      skipped: ["Skipped", "skipped"], done: ["Done", "done"],
    };
    const [label, cls] = map[st] ?? map.new;
    return `<span class="tour-chip tour-chip--${cls}">${st === "done" ? "✓ " : ""}${label}</span>`;
  }

  _cta(st) {
    if (st === "active" || st === "paused") return "Resume";
    if (st === "done") return "Replay";
    return "Start";
  }

  openHub() {
    if (this._run) this.pause({ quiet: true });
    this._closeHub();
    const tours = this._order.map((id) => this._tours.get(id)).filter((t) => !t.can || this._safe(() => t.can(this._api)));
    const featured = tours.filter((t) => t.featured);
    const rest = tours.filter((t) => !t.featured);

    const heroMarkup = featured.map((t) => {
      const p = this.progress(t.id);
      const chapters = t.chapters ?? [];
      const done = new Set(p.chapters);
      const doneCount = chapters.filter((c) => done.has(c.id)).length;
      return `
        <section class="tour-hub-hero">
          <div class="tour-hub-hero-head">
            <span class="tour-hub-hero-icon" aria-hidden="true">${t.icon}</span>
            <div class="tour-hub-hero-text">
              <div class="tour-hub-hero-title">${t.title} ${this._statusChip(p.status)}</div>
              <p class="tour-hub-hero-sub">${t.description}</p>
            </div>
            <button class="tour-btn tour-btn--primary" data-hub-start="${t.id}" type="button">${this._cta(p.status)}</button>
          </div>
          <div class="tour-hub-hero-bar" aria-label="${doneCount} of ${chapters.length} parts done"><span style="width:${chapters.length ? (doneCount / chapters.length) * 100 : 0}%"></span></div>
          <ol class="tour-hub-chapters">
            ${chapters.map((c, i) => `
              <li>
                <button class="tour-hub-chapter${done.has(c.id) ? " tour-hub-chapter--done" : ""}" data-hub-start="${t.id}" data-hub-chapter="${c.id}" type="button">
                  <span class="tour-hub-chapter-num" aria-hidden="true">${done.has(c.id) ? "✓" : i + 1}</span>
                  <span class="tour-hub-chapter-text"><span class="tour-hub-chapter-title">${c.title}</span><span class="tour-hub-chapter-sub">${c.blurb ?? ""}</span></span>
                </button>
              </li>`).join("")}
          </ol>
        </section>`;
    }).join("");

    const cards = rest.map((t) => {
      const p = this.progress(t.id);
      return `
        <button class="tour-hub-card" data-hub-start="${t.id}" type="button">
          <span class="tour-hub-card-icon" aria-hidden="true">${t.icon}</span>
          <span class="tour-hub-card-text">
            <span class="tour-hub-card-title">${t.title}</span>
            <span class="tour-hub-card-sub">${t.description}</span>
          </span>
          ${this._statusChip(p.status)}
        </button>`;
    }).join("");

    const el = document.createElement("div");
    el.className = "tour-hub-overlay";
    el.innerHTML = `
      <div class="tour-hub" role="dialog" aria-modal="true" aria-labelledby="tourHubTitle">
        <header class="tour-hub-head">
          <div>
            <h2 class="tour-hub-title" id="tourHubTitle">Tutorials</h2>
            <p class="tour-hub-sub">Short walkthroughs inside the real app. Close any of them midway and pick up right where you left off.</p>
          </div>
          <button class="tour-hub-close" type="button" aria-label="Close">&times;</button>
        </header>
        <div class="tour-hub-scroll">
          ${heroMarkup}
          ${cards ? `<h3 class="tour-hub-label">Feature guides</h3><div class="tour-hub-grid">${cards}</div>` : ""}
          <p class="tour-hub-foot">Tip: look for the <span class="tour-help-btn tour-help-btn--inline" aria-hidden="true">?</span> next to a screen's title to get its guide right there.</p>
        </div>
      </div>`;
    el.addEventListener("click", (e) => {
      if (e.target === el || e.target.closest(".tour-hub-close")) { this._closeHub(); return; }
      const b = e.target.closest("[data-hub-start]");
      if (!b) return;
      const id = b.dataset.hubStart;
      const chapter = b.dataset.hubChapter;
      this._closeHub();
      if (chapter) this.start(id, { chapter });
      else this.start(id, { fromStart: this.progress(id).status === "done" || this.progress(id).status === "skipped" });
    });
    document.body.appendChild(el);
    this._hubEl = el;
    requestAnimationFrame(() => el.querySelector(".tour-hub-close")?.focus());
  }

  _closeHub() {
    this._hubEl?.remove();
    this._hubEl = null;
  }
}

export default new TourEngine();
